// ============================================================================
// CrewAhead Maritime Platform - Content API Functions
// ============================================================================
// Public content endpoints: blog posts, FAQs, static pages, testimonials,
// and contact/newsletter forms. No authentication required for reads.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import { API_BASE_URL } from '../api-config';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  featuredImage: string | null;
  featuredImageUrl: string | null;
  category: {
    id: string;
    name: string;
    slug: string;
  } | null;
  tags: Array<{ id: string; name: string; slug: string }>;
  source: {
    id: string;
    name: string;
    slug: string;
  } | null;
  author: string | null;
  contentType: string;
  isFeatured: boolean;
  isKnowledgeHub: boolean;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  viewCount: number;
  seoTitle: string | null;
  metaDescription: string | null;
  readingTimeMinutes: number | null;
}

/**
 * Resolve the effective featured image URL for a blog post.
 * Prefers the external URL field; falls back to the uploaded file path.
 */
export function getBlogPostImageUrl(post: BlogPost): string | null {
  if (post.featuredImageUrl) return post.featuredImageUrl;
  if (post.featuredImage) {
    return `${API_BASE_URL}${post.featuredImage}`;
  }
  return null;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isActive: boolean;
  children: BlogCategory[];
}

export interface BlogTag {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

export interface BlogSource {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  isActive: boolean;
  order: number;
}

export interface StaticPage {
  id: string;
  title: string;
  slug: string;
  content: string;
  isPublished: boolean;
  metaDescription: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string | null;
  content: string;
  roleType: 'seafarer' | 'shore' | 'employer';
  avatarUrl: string | null;
  isActive: boolean;
  rating: number | null;
}

export interface CompanySettings {
  companyName: string;
  logoUrl: string | null;
  primaryColor: string;
  supportEmail: string;
  supportPhone: string | null;
  socialLinks: Record<string, string>;
}

export interface KnowledgeHubData {
  featured: BlogPost[];
  latest: BlogPost[];
  grouped: Array<{
    category: BlogCategory;
    posts: BlogPost[];
  }>;
}

export interface ContactEnquiryInput {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  enquiryType: string;
  subject?: string;
  message: string;
}

export interface NewsletterSubscriptionInput {
  email: string;
}

export interface ContactEnquiryResponse {
  detail: string;
  id: number;
  emailSent: boolean;
}

export interface NewsletterSubscriptionResponse {
  detail: string;
  email?: string;
  unsubscribeToken?: string;
}

export interface PublicStats {
  // Platform depth
  courses: number;
  shorePositions: number;
  shorePositionsWithGuidance: number;
  skillGapRules: number;
  skills: number;
  ranks: number;
  certifications: number;
  vesselTypes: number;
  vesselCategories: number;
  countries: number;
  // Marketplace
  seaJobs: number;
  shoreJobs: number;
  employers: number;
  trainingInstitutes: number;
  courseOfferings: number;
  openBatches: number;
  availableSeats: number;
  seafarers: number;
  shoreProfessionals: number;
  // Legacy keys
  activeSeafarers: number;
  activeShoreProfessionals: number;
  openPositions: number;
  generatedAt?: string;
}

// ─── Admin Content Types ───────────────────────────────────────────────────────

export type BlogPostStatus = 'draft' | 'published' | 'archived';

export interface BlogPostAdmin {
  id: string;
  title: string;
  slug: string;
  summary: string;
  content: string;
  contentType: string;
  status: BlogPostStatus;
  category: string | number | null;
  tagIds?: Array<string | number>;
  tagList: BlogTag[];
  source: string | number | null;
  sourceName: string;
  sourceUrl: string;
  featuredImageUrl: string | null;
  featuredImage?: File | null;
  seoTitle: string;
  metaDescription: string;
  authorName: string;
  publishedAt: string | null;
  readingTimeMinutes: number;
  targetRanks: string[];
  targetVesselTypes: string[];
  targetExperienceLevel: string;
  isFeatured: boolean;
  isKnowledgeHub: boolean;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminListPostsParams {
  status?: BlogPostStatus;
  contentType?: string;
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AiRewritePayload {
  rawText: string;
  targetKeywords?: string[];
  tone?: string;
}

export interface AiRewriteResponse {
  rewrittenContent: string;
  seoTitle: string;
  metaDescription: string;
  suggestedTags: string[];
}

export interface BlogCategoryInput {
  name: string;
  description?: string;
  parent?: string | number;
  icon?: string;
  color?: string;
  displayOrder?: number;
}

export interface BlogTagInput {
  name: string;
  description?: string;
}

export interface BlogSourceInput {
  name: string;
  websiteUrl?: string;
  description?: string;
}

// ─── Admin Posts ───────────────────────────────────────────────────────────────

/**
 * GET /content/admin/posts/
 *
 * List all blog posts (all statuses) for admin.
 */
export async function adminListPosts(params?: AdminListPostsParams): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: BlogPostAdmin[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.contentType) query.set('content_type', params.contentType);
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CONTENT.ADMIN.POSTS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      next: string | null;
      previous: string | null;
      results: BlogPostAdmin[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /content/admin/posts/{slug}/
 *
 * Get a single blog post (admin view).
 */
export async function adminGetPost(slug: string): Promise<BlogPostAdmin> {
  try {
    const response = await apiClient.get<BlogPostAdmin>(
      API_ENDPOINTS.CONTENT.ADMIN.POST_DETAIL(slug)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

function buildPostFormData(data: Partial<BlogPostAdmin> & { featuredImage?: File }): FormData {
  const formData = new FormData();
  const append = (key: string, value: unknown) => {
    if (value === undefined || value === null) return;
    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((item) => formData.append(key, String(item)));
    } else if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false');
    } else {
      formData.append(key, String(value));
    }
  };

  append('title', data.title);
  append('summary', data.summary);
  append('content', data.content);
  append('content_type', data.contentType);
  append('status', data.status);
  append('category', data.category);
  append('source', data.source);
  append('source_name', data.sourceName);
  append('source_url', data.sourceUrl);
  append('featured_image_url', data.featuredImageUrl);
  append('seo_title', data.seoTitle);
  append('meta_description', data.metaDescription);
  append('author_name', data.authorName);
  append('target_ranks', data.targetRanks);
  append('target_vessel_types', data.targetVesselTypes);
  append('target_experience_level', data.targetExperienceLevel);
  append('is_featured', data.isFeatured);
  append('is_knowledge_hub', data.isKnowledgeHub);
  if (data.tagIds && data.tagIds.length > 0) {
    data.tagIds.forEach((id) => formData.append('tag_ids', String(id)));
  }
  if (data.featuredImage) {
    formData.append('featured_image', data.featuredImage);
  }
  return formData;
}

/**
 * POST /content/admin/posts/
 *
 * Create a new blog post. Uses multipart/form-data if a featured image file is
 * provided; otherwise uses JSON.
 */
export async function adminCreatePost(
  data: Partial<BlogPostAdmin> & { featuredImage?: File }
): Promise<BlogPostAdmin> {
  try {
    const hasFile = data.featuredImage instanceof File;
    const payload = hasFile ? buildPostFormData(data) : data;
    const response = await apiClient.post<BlogPostAdmin>(
      API_ENDPOINTS.CONTENT.ADMIN.POSTS,
      payload,
      hasFile ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /content/admin/posts/{slug}/
 *
 * Partially update a blog post. Uses multipart/form-data if a featured image
 * file is provided; otherwise uses JSON.
 */
export async function adminUpdatePost(
  slug: string,
  data: Partial<BlogPostAdmin> & { featuredImage?: File }
): Promise<BlogPostAdmin> {
  try {
    const hasFile = data.featuredImage instanceof File;
    const payload = hasFile ? buildPostFormData(data) : data;
    const response = await apiClient.patch<BlogPostAdmin>(
      API_ENDPOINTS.CONTENT.ADMIN.POST_DETAIL(slug),
      payload,
      hasFile ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /content/admin/posts/{slug}/
 *
 * Delete a blog post.
 */
export async function adminDeletePost(slug: string): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.CONTENT.ADMIN.POST_DETAIL(slug));
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /content/admin/posts/{slug}/publish
 *
 * Publish a blog post.
 */
export async function adminPublishPost(slug: string): Promise<{ detail: string; slug: string }> {
  try {
    const response = await apiClient.post<{ detail: string; slug: string }>(
      API_ENDPOINTS.CONTENT.ADMIN.PUBLISH(slug)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /content/admin/posts/{slug}/unpublish
 *
 * Unpublish a blog post (set status to draft).
 */
export async function adminUnpublishPost(
  slug: string
): Promise<{ detail: string; slug: string }> {
  try {
    const response = await apiClient.post<{ detail: string; slug: string }>(
      API_ENDPOINTS.CONTENT.ADMIN.UNPUBLISH(slug)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /content/admin/posts/{slug}/archive
 *
 * Archive a blog post.
 */
export async function adminArchivePost(slug: string): Promise<{ detail: string; slug: string }> {
  try {
    const response = await apiClient.post<{ detail: string; slug: string }>(
      API_ENDPOINTS.CONTENT.ADMIN.ARCHIVE(slug)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Admin Categories ──────────────────────────────────────────────────────────

/**
 * GET /content/admin/categories/
 *
 * List all blog categories for admin.
 */
export async function adminListCategories(): Promise<BlogCategory[]> {
  try {
    const response = await apiClient.get<BlogCategory[]>(
      API_ENDPOINTS.CONTENT.ADMIN.CATEGORIES
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /content/admin/categories/
 *
 * Create a new blog category.
 */
export async function createCategory(data: BlogCategoryInput): Promise<BlogCategory> {
  try {
    const response = await apiClient.post<BlogCategory>(
      API_ENDPOINTS.CONTENT.ADMIN.CATEGORIES,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Admin Tags ────────────────────────────────────────────────────────────────

/**
 * GET /content/admin/tags/
 *
 * List all blog tags for admin.
 */
export async function adminListTags(): Promise<BlogTag[]> {
  try {
    const response = await apiClient.get<BlogTag[]>(API_ENDPOINTS.CONTENT.ADMIN.TAGS);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /content/admin/tags/
 *
 * Create a new blog tag.
 */
export async function createTag(data: BlogTagInput): Promise<BlogTag> {
  try {
    const response = await apiClient.post<BlogTag>(API_ENDPOINTS.CONTENT.ADMIN.TAGS, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Admin Sources ───────────────────────────────────────────────────────────────

/**
 * GET /content/admin/sources/
 *
 * List all content sources for admin.
 */
export async function adminListSources(): Promise<BlogSource[]> {
  try {
    const response = await apiClient.get<BlogSource[]>(API_ENDPOINTS.CONTENT.ADMIN.SOURCES);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /content/admin/sources/
 *
 * Create a new content source.
 */
export async function createSource(data: BlogSourceInput): Promise<BlogSource> {
  try {
    const response = await apiClient.post<BlogSource>(
      API_ENDPOINTS.CONTENT.ADMIN.SOURCES,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Admin AI Rewrite / SEO Assist ─────────────────────────────────────────────

/**
 * POST /content/admin/posts/ai-rewrite
 *
 * Rewrite raw draft content using the AI rewrite / SEO assist endpoint.
 */
export async function aiRewriteContent(
  payload: AiRewritePayload
): Promise<AiRewriteResponse> {
  try {
    const response = await apiClient.post<AiRewriteResponse>(
      API_ENDPOINTS.CONTENT.ADMIN.AI_REWRITE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Blog Posts ──────────────────────────────────────────────────────────────

/**
 * GET /content/posts/
 *
 * List published blog posts with filtering, search, and ordering.
 *
 * @param params - Optional filters, search, and pagination
 * @returns Paginated list of blog posts
 */
export async function getBlogPosts(params?: {
  category?: string;
  tag?: string;
  contentType?: string;
  source?: string;
  isFeatured?: boolean;
  isKnowledgeHub?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: BlogPost[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.tag) query.set('tag', params.tag);
    if (params?.contentType) query.set('content_type', params.contentType);
    if (params?.source) query.set('source', params.source);
    if (params?.isFeatured) query.set('is_featured', 'true');
    if (params?.isKnowledgeHub) query.set('is_knowledge_hub', 'true');
    if (params?.search) query.set('search', params.search);
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CONTENT.POSTS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      next: string | null;
      previous: string | null;
      results: BlogPost[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /content/posts/{slug}/
 *
 * Get a single published blog post by slug.
 * Atomically increments view count on each request.
 *
 * @param slug - Post slug
 * @returns Blog post detail
 */
export async function getBlogPost(slug: string): Promise<BlogPost> {
  try {
    const response = await apiClient.get<BlogPost>(
      API_ENDPOINTS.CONTENT.POST_DETAIL(slug)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Categories ──────────────────────────────────────────────────────────────

/**
 * GET /content/categories/
 *
 * List all active blog categories with their children.
 *
 * @returns List of categories
 */
export async function getBlogCategories(): Promise<BlogCategory[]> {
  try {
    const response = await apiClient.get<BlogCategory[]>(
      API_ENDPOINTS.CONTENT.CATEGORIES
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Tags ────────────────────────────────────────────────────────────────────

/**
 * GET /content/tags/
 *
 * List all active blog tags.
 *
 * @returns List of tags
 */
export async function getBlogTags(): Promise<BlogTag[]> {
  try {
    const response = await apiClient.get<BlogTag[]>(
      API_ENDPOINTS.CONTENT.TAGS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Sources ─────────────────────────────────────────────────────────────────

/**
 * GET /content/sources/
 *
 * List all active content sources.
 *
 * @returns List of sources
 */
export async function getBlogSources(): Promise<BlogSource[]> {
  try {
    const response = await apiClient.get<BlogSource[]>(
      API_ENDPOINTS.CONTENT.SOURCES
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Knowledge Hub ───────────────────────────────────────────────────────────

/**
 * GET /content/knowledge-hub/
 *
 * Get content for the Knowledge Hub page.
 * Returns featured articles, latest updates, and posts grouped by category.
 *
 * @returns Knowledge hub data
 */
export async function getKnowledgeHub(): Promise<KnowledgeHubData> {
  try {
    const response = await apiClient.get<KnowledgeHubData>(
      API_ENDPOINTS.CONTENT.KNOWLEDGE_HUB
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Related Posts ───────────────────────────────────────────────────────────

/**
 * GET /content/related/{slug}/
 *
 * Get related posts for a given post slug.
 * Finds posts in the same category or with shared tags.
 *
 * @param slug - Post slug to find related content for
 * @param params - Optional pagination
 * @returns Paginated list of related posts
 */
export async function getRelatedPosts(
  slug: string,
  params?: { page?: number; pageSize?: number }
): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: BlogPost[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CONTENT.RELATED(slug)}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      next: string | null;
      previous: string | null;
      results: BlogPost[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── FAQs ────────────────────────────────────────────────────────────────────

/**
 * GET /content/faqs/
 *
 * List active FAQs, optionally filtered by category or search query.
 *
 * @param params - Optional category filter and search
 * @returns List of FAQs
 */
export async function getFAQs(params?: {
  category?: string;
  search?: string;
}): Promise<FAQ[]> {
  try {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CONTENT.FAQS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<FAQ[]>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Static Pages ────────────────────────────────────────────────────────────

/**
 * GET /content/pages/
 *
 * List all published static pages.
 *
 * @returns List of static pages
 */
export async function getStaticPages(): Promise<StaticPage[]> {
  try {
    const response = await apiClient.get<StaticPage[]>(
      API_ENDPOINTS.CONTENT.PAGES
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /content/pages/{slug}/
 *
 * Get a single published static page by slug.
 *
 * @param slug - Page slug
 * @returns Static page detail
 */
export async function getStaticPage(slug: string): Promise<StaticPage> {
  try {
    const response = await apiClient.get<StaticPage>(
      API_ENDPOINTS.CONTENT.PAGE_DETAIL(slug)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Testimonials ────────────────────────────────────────────────────────────

/**
 * GET /content/testimonials/
 *
 * List active testimonials, optionally filtered by role type.
 *
 * @param roleType - Optional role type filter
 * @returns List of testimonials
 */
export async function getTestimonials(
  roleType?: 'seafarer' | 'shore' | 'employer'
): Promise<Testimonial[]> {
  try {
    const query = new URLSearchParams();
    if (roleType) query.set('role_type', roleType);

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CONTENT.TESTIMONIALS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<Testimonial[]>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Company Settings ────────────────────────────────────────────────────────

/**
 * GET /content/settings/
 *
 * Get public company settings (singleton).
 *
 * @returns Company settings
 */
export async function getCompanySettings(): Promise<CompanySettings> {
  try {
    const response = await apiClient.get<CompanySettings>(
      API_ENDPOINTS.CONTENT.SETTINGS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Contact ─────────────────────────────────────────────────────────────────

/**
 * POST /content/contact/
 *
 * Submit a contact enquiry. Saves to DB and sends admin notification email.
 *
 * @param data - Contact enquiry data
 * @returns Submission confirmation
 */
export async function submitContactEnquiry(
  data: ContactEnquiryInput
): Promise<ContactEnquiryResponse> {
  try {
    const response = await apiClient.post<ContactEnquiryResponse>(
      API_ENDPOINTS.CONTENT.CONTACT,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Newsletter ──────────────────────────────────────────────────────────────

/**
 * POST /content/newsletter/
 *
 * Subscribe to the newsletter. Deduplicates by email.
 *
 * @param data - Newsletter subscription data
 * @returns Subscription confirmation
 */
export async function subscribeNewsletter(
  data: NewsletterSubscriptionInput
): Promise<NewsletterSubscriptionResponse> {
  try {
    const response = await apiClient.post<NewsletterSubscriptionResponse>(
      API_ENDPOINTS.CONTENT.NEWSLETTER,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Public Homepage Stats ───────────────────────────────────────────────────

/**
 * GET /public/stats/
 *
 * Public aggregate counts for the marketing homepage.
 * Results are cached on the backend for 15 minutes.
 *
 * @param options - Optional request configuration (e.g. timeout)
 * @returns Active seafarers, active shore professionals, and open positions.
 */
export async function getPublicStats(options?: { timeout?: number }): Promise<PublicStats> {
  try {
    const response = await apiClient.get<PublicStats>(
      API_ENDPOINTS.PUBLIC.STATS,
      { timeout: options?.timeout }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
